import { useEffect, useRef, useState } from 'react';
import {
  usePatchSalesRepMutation,
  useDeleteSalesRepMutation,
} from 'app/services/projectBoardApi';
import type { ICompany, IContact, ISalesRep } from 'models';
import SalesNumberInput from './SalesNumberInput';
import DeleteRepPopup from './DeleteRepPopup';

/**
 * SalesRepUnitView (`sales/view/sales_rep_unit.js` + sales_rep_unit.tmpl live
 * part — §3.9 / parity 81–83): one added-rep row =
 * `div.rep.project-sales-tab-content-table.padding-row.row` with company /
 * contact selects ("None" `- -` option), the type-3 commission pair
 * (% decimal 0–100 / US$ integer 0–10M) or an empty .col-xs-4, and the
 * bootstrap "more actions" dropdown holding Delete.
 *
 * changeInput: 'None'/'' → null; INPUT → decoded number (getValue), SELECT →
 * Number; PATCH /api/v1.0/projects/sales_reps/{id} with
 * `{[name]: value, generate_id, sales_id}`; the response's `set_values`
 * (server-recomputed splits) overlays the cached row for display (the
 * legacy `unitModel` — merged by the api layer; risk 7).
 *
 * [NEG] honored: the phantom `open_editing_popup` handler (undefined on the
 * view — events-hash entry dropped) is not rebuilt; the dropdown toggle is
 * the bootstrap behavior itself (open on click, close on outside click).
 * Legacy commented markup variants (parity 87) omitted.
 */
interface RepUnitRowProps {
  rep: ISalesRep;
  salesId: number;
  generateId: string | null;
  companies: ICompany[];
  companyContacts: Map<number, IContact[]>;
  isType3: boolean;
  locked: boolean;
}

const RepUnitRow = ({
  rep,
  salesId,
  generateId,
  companies,
  companyContacts,
  isType3,
  locked,
}: RepUnitRowProps) => {
  const [patchRep] = usePatchSalesRepMutation();
  const [deleteRep] = useDeleteSalesRepMutation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  // Bootstrap dropdown semantics: close on any outside click.
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDoc = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('click', onDoc);
    return () => document.removeEventListener('click', onDoc);
  }, [menuOpen]);

  const commit = (name: string, value: number | null) => {
    patchRep({
      id: rep.id,
      salesId,
      body: {
        [name]: value,
        generate_id: generateId,
        sales_id: rep.sales_id ?? salesId,
      },
    });
  };

  const onSelect = (name: string, raw: string) => {
    // 'None'/'' → null, else Number (changeInput SELECT branch).
    const value = raw === 'None' || raw === '' ? null : Number(raw);
    commit(name, Number.isNaN(value as number) ? null : value);
  };

  const repContacts = rep.rep_co_id != null ? companyContacts.get(Number(rep.rep_co_id)) ?? [] : [];

  return (
    <div className="rep project-sales-tab-content-table padding-row row">
      <div className="col-xs-3">
        <select
          className="medium-input"
          name="rep_co_id"
          required
          disabled={locked}
          value={rep.rep_co_id != null ? String(rep.rep_co_id) : 'None'}
          onChange={(e) => onSelect('rep_co_id', e.target.value)}
        >
          <option value="None">- -</option>
          {companies.map((c) => (
            <option key={c.id} value={String(c.id)}>
              {c.name}
            </option>
          ))}
        </select>
      </div>
      <div className="col-xs-3">
        <select
          className="small-input"
          name="rep_contact_person_id"
          required
          disabled={locked}
          value={rep.rep_contact_person_id != null ? String(rep.rep_contact_person_id) : 'None'}
          onChange={(e) => onSelect('rep_contact_person_id', e.target.value)}
        >
          <option value="None">- -</option>
          {repContacts.map((c) => (
            <option key={c.id} value={String(c.id)}>
              {c.name}
            </option>
          ))}
        </select>
      </div>
      {isType3 ? (
        <div className="col-xs-4">
          <div className="row">
            <div className="col-xs-2 text-right no-right-padding">
              <span className="unit">%</span>
            </div>
            <div className="col-xs-4">
              <SalesNumberInput
                name="rep_co_commission_percent"
                value={rep.rep_co_commission_percent}
                formatter="decimal"
                className="xxs-input input-number"
                step="0.01"
                min={0}
                max={100}
                disabled={locked}
                onCommit={commit}
              />
            </div>
            <div className="col-xs-2 text-right no-right-padding">
              <span className="unit">US$</span>
            </div>
            <div className="col-xs-4">
              <SalesNumberInput
                name="rep_co_commission_amount"
                value={rep.rep_co_commission_amount}
                formatter="integer"
                className="xs-input input-number"
                step="1"
                min={0}
                max={10000000}
                disabled={locked}
                onCommit={commit}
              />
            </div>
          </div>
        </div>
      ) : (
        <div className="col-xs-4" />
      )}
      <div
        className={`dropdown dropdownprop-format col-xs-2 text-right${menuOpen ? ' open' : ''}`}
        ref={menuRef}
      >
        <div className="row">
          <button
            className="btn dropdown-toggle icon-more-actions btn-more"
            type="button"
            data-toggle="dropdown"
            disabled={locked}
            onClick={(e) => {
              e.stopPropagation();
              setMenuOpen((o) => !o);
            }}
          />
          <ul
            className="dropdown-menu edit-popup"
            style={menuOpen ? { display: 'block' } : undefined}
          >
            <li>
              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
              <a
                tabIndex={-1}
                href="#"
                id="deleteRep"
                className="delete "
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setMenuOpen(false);
                  setDeleting(true);
                }}
              >
                <i className="icon-trash" />
                Delete
              </a>
            </li>
          </ul>
        </div>
      </div>
      {deleting && (
        <DeleteRepPopup
          onConfirm={() => {
            deleteRep({ id: rep.id, salesId });
            setDeleting(false);
          }}
          onClose={() => setDeleting(false)}
        />
      )}
    </div>
  );
};

export default RepUnitRow;
